import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload } from "lucide-react";

const HostVerificationPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [governmentId, setGovernmentId] = useState<File | null>(null);
  const [addressProof, setAddressProof] = useState<File | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      if (governmentId) {
        const govIdFileName = `${user.id}-gov-id.${governmentId.name.split('.').pop()}`;
        const { error: govIdError } = await supabase.storage
          .from('verification-documents')
          .upload(govIdFileName, governmentId);
        if (govIdError) throw govIdError;
      }

      if (addressProof) {
        const addressFileName = `${user.id}-address.${addressProof.name.split('.').pop()}`;
        const { error: addressError } = await supabase.storage
          .from('verification-documents')
          .upload(addressFileName, addressProof);
        if (addressError) throw addressError;
      }

      // Update host verification requirements
      const { error: verificationError } = await supabase
        .from('host_verification_requirements')
        .upsert({
          host_id: user.id,
          government_id_submitted: !!governmentId,
          address_proof_submitted: !!addressProof,
        });

      if (verificationError) throw verificationError;

      // Update onboarding step
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          host_onboarding_step: 3,
          host_onboarding_data: {
            verification: true,
            profile: true
          }
        })
        .eq('id', user.id);

      if (profileError) throw profileError;

      toast({
        title: "Documents submitted",
        description: "Your verification documents have been submitted for review.",
      });

      navigate("/host/settings/payment-methods/add");
    } catch (error) {
      console.error('Error submitting verification:', error);
      toast({
        title: "Error",
        description: "Failed to submit verification documents. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">Host Verification</h1>
      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="governmentId">Government ID</Label>
              <Input
                id="governmentId"
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setGovernmentId(e.target.files?.[0] || null)}
                required
              />
              <p className="text-sm text-muted-foreground mt-1">
                Upload a clear photo or scan of your government-issued ID
              </p>
            </div>

            <div>
              <Label htmlFor="addressProof">Proof of Address</Label>
              <Input
                id="addressProof"
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setAddressProof(e.target.files?.[0] || null)}
                required
              />
              <p className="text-sm text-muted-foreground mt-1">
                Upload a recent utility bill or bank statement showing your address
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/host/onboarding")}
            >
              Back
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit Documents
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default HostVerificationPage;